/**
 * The shared kit's own words.
 *
 * These are the labels a component ships with when the caller does not pass its
 * own: the table's footer line, a drawer's close button, the Excel import's
 * standing instructions. A feature that needs different wording still overrides
 * it through props — this is only the default.
 */

/** dtLanguage — _AdminsBaseLayout.cshtml:1152-1167. */
export const table = {
  search: 'ابحث:',
  lengthBefore: 'أظهر',
  lengthAfter: 'مدخلات',
  zeroRecords: 'لا توجد مدخلات في النظام',
  loadFailed: 'تعذّر تحميل البيانات',
  processing: 'جارٍ التحميل...',
  info: (start, end, total) => `إظهار ${start} إلى ${end} من أصل ${total} مدخل`,
  infoEmpty: 'يعرض 0 إلى 0 من أصل 0 سجل',
  infoFiltered: (max) => `(منتقاة من مجموع ${max} مُدخل)`,
  previous: 'السابق',
  next: 'التالي',
  pagination: 'ترقيم الصفحات',
}

/** Words the kit reuses across many components. */
export const kit = {
  /* GroupPicker — shared by five forms, so its own wording lives with the kit. */
  pickerMore: (shown, total) => `عرض ${shown} من ${total} — حدّد البحث للوصول إلى الباقي`,
  pickerChosen: 'إدارة مختارة',
  pickerEmpty: 'لا توجد إدارة بهذا الاسم',
  back: 'رجوع',
  nextStep: 'التالي',
  previousStep: 'السابق',
  close: 'إغلاق', // Resources.Close
  confirmDelete: 'تأكيد الحذف',
  /* Type-to-confirm, for a delete that cannot be undone — see ConfirmDialog. */
  confirmByTyping: 'للتأكيد قم بكتابة الاسم',
  copy: 'نسخ', // Resources.Copy
  copied: 'تم النسخ',
  delete: 'حذف', // Resources.Delete
  cancel: 'إلغاء', // Resources.Cancel
  export: 'تصدير', // Resources.Export
  options: 'خيارات',
  moreInfo: 'مزيد من المعلومات',
  clearSearch: 'مسح البحث',
  /* The filter inside a long dropdown — see Select. */
  selectSearch: 'ابحث…',
  selectNoMatches: 'لا توجد نتائج',
  clearDate: 'مسح التاريخ',
  remove: 'إزالة',
  chooseFile: 'اختيار ملف',
  noFile: 'لم يتم اختيار ملف',
  loading: 'يتم جلب البيانات من الخادم',
  /** The list separator — an Arabic comma here, a Latin one in English. */
  listSep: '، ',
}

export const excel = {
  intro: 'يُرجى رفع ملف بصيغة Excel (.xlsx) أو CSV (.csv)',
  /* The topic import is not an excel import at all - it takes a ZIP exported from
     another installation, and the server rejects anything else. */
  introZip: 'يُرجى رفع ملف مضغوط بصيغة ZIP (.zip)',
  columnsIntro: 'يجب أن يحتوي الملف على الأعمدة التالية',
  mandatory: 'إجباري',
  optional: 'إختياري',
  notesTitle: 'ملاحظات مهمة',
  notes: 'حمّل القالب أولاً، وأبقِ أسماء الأعمدة وترتيبها كما هي في الملف.',
  notesZip: 'الملف المضغوط هو ما تُصدّره هذه الشاشة من تثبيت آخر؛ لا يوجد قالب لتحميله. الموضوع الذي يحمل اسماً موجوداً مسبقاً لا يُستورد.',
  template: 'تحميل القالب',
  save: 'رفع',
  close: 'إغلاق',
  fileRequired: 'فضلاً اختر ملف إكسل',
}

/** `getStatusString`, _MessagesModal.cshtml:249-266. */
export const notificationStates = {
  Waiting: 'في الإنتظار',
  Running: 'جارية',
  Stopped: 'توقفت',
  Finished: 'إنتهت',
  Failed: 'فشلت',
  Succeeded: 'نجحت',
}

export const notifications = {
  title: 'الإشعارات', // Resources.Notifications
  details: 'التفاصيل', // Resources.Details
  preview: 'عرض', // Resources.Preview
  state: 'الحاله', // Resources.Status
  scheduledOn: 'تاريخ الجدولة', // Resources.CreatedOn
  startDate: 'تاريخ البداية', // Resources.StartDate
  endDate: 'تاريخ النهاية', // Resources.EndDate
  recipient: 'المستلم',
  deliveryStatus: 'التسليم',
  tries: 'المحاولات',
  error: 'الخطأ',
  noMessages: 'لم ينتج عن هذه الدفعة أي رسائل.',
  loadFailed: 'تعذر تحميل سجل الإشعارات.',
}

/** The inline rich-text toolbar. */
export const editor = {
  bold: 'عريض',
  italic: 'مائل',
  underline: 'تسطير',
  heading: 'عنوان',
  bullets: 'قائمة',
  link: 'رابط',
  linkPrompt: 'الرابط',
  clear: 'إزالة التنسيق',
}

/** The light/dark control. `الوضع` is the legacy header's own word for it. */
export const theme = {
  label: 'الوضع',
  light: 'فاتح',
  dark: 'داكن',
}

/**
 * What the TRANSPORT says when it has to speak for itself.
 *
 * `src/lib/{api,upload,download}.js` raise these — a network failure, a redirect into HTML,
 * a lapsed session, a refused role, a bare HTTP status. They are NOT server messages, so
 * the "show it as received" rule (W6) does not cover them: they were hard-coded English and
 * an Arabic panel showed `Request failed (502)` on any outage.
 */
export const transport = {
  network: 'تعذّر الاتصال بالخادم، تحقق من الاتصال ثم أعد المحاولة',
  networkUpload: 'تعذّر الاتصال بالخادم أثناء رفع الملف',
  redirected: 'تعذّر إتمام الطلب، فضلا أعد تحميل الصفحة',
  sessionEnded: 'انتهت الجلسة، فضلا سجّل الدخول مرة أخرى',
  denied: 'لا تملك الصلاحيات لتنفيذ هذه العملية',
  deniedDownload: 'لا تملك الصلاحيات لتحميل هذا الملف',
  html: 'تعذّرت قراءة رد الخادم، فضلا أعد المحاولة',
  /** `(502)` — the code stays, it is what a support call is opened with. */
  failed: (status) => `تعذّر إتمام الطلب (${status})`,
  uploadFailed: (status) => `تعذّر رفع الملف (${status})`,
}

/**
 * The page-level activation switch's outcome, said the same way on every page
 * that carries one.
 *
 * These are UI chrome, not server copy. Four pages each toasted (or did not
 * toast) their own wording, so activating surveys and activating policies
 * announced themselves differently — or, on three of the four, not at all. The
 * SERVER's message is still what a FAILURE says, because only the server knows
 * why; success is a state the UI already knows it asked for.
 */
export const feature = {
  enabled: 'تم تفعيل الخاصية',
  disabled: 'تم إيقاف الخاصية',
  failed: 'تعذّر تنفيذ العملية',
}
